import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import './localOpsProofLanding.css';

const PROOF_LINE = 'Post a Local Task. Watch it get done.';
const CTA_HEADLINE = 'Need something done?';
const CTA_BODY =
  'Post a local task and nearby helpers can accept. You approve completion.';
const CTA_FOOTER = 'Local help. Real people. Action nearby.';
const VERIFICATION_COPY = 'Verified completion through JG LocalOps';
const FALLBACK_HELPER_COPY = 'Completed by a local JG LocalOps helper';
const FALLBACK_SUMMARY =
  'Posted, accepted, and completed through JG LocalOps.';

const stripApi = (url) =>
  typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';

const getBackendOrigin = () =>
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  'http://localhost:4000';

const safeText = (value, fallback = '') => {
  const clean = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean || fallback;
};

const buildPostTaskPath = (regionSlug) => {
  const params = new URLSearchParams({
    intent: 'post_task',
    source: 'completion_proof_share',
  });
  if (regionSlug) params.set('regionSlug', regionSlug);
  return `/playermap?${params.toString()}`;
};

const normalizeMedia = (proof) => {
  const media = Array.isArray(proof?.safeMedia) ? proof.safeMedia : [];
  return media.find((item) => item?.url) || null;
};

const formatDuration = (value) => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';
  const rounded = Math.round(totalSeconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const buildProofHeadline = (proof, regionLabel) => {
  const label = safeText(proof?.taskLabel, 'Local task completed');
  const completedLabel = /\bcompleted\b/i.test(label)
    ? label
    : `${label} completed`;
  return `${completedLabel} in ${regionLabel}`;
};

const buildVelocityLine = (proof) => {
  const acceptedDuration = formatDuration(
    proof?.acceptedInSeconds ?? proof?.velocity?.acceptedInSeconds,
  );
  const completedDuration = formatDuration(
    proof?.completedInSeconds ?? proof?.velocity?.completedInSeconds,
  );
  const parts = [];
  if (acceptedDuration) parts.push(`Accepted in ${acceptedDuration}`);
  if (completedDuration) parts.push(`Completed in ${completedDuration}`);
  return parts.length ? parts : [];
};

const shortenProofId = (value) => {
  const raw = safeText(value);
  if (!raw) return '';
  if (raw.length <= 12) return raw;
  return `${raw.slice(0, 6)}...${raw.slice(-4)}`;
};

const getHelperRewardCopy = (proof) => {
  if (proof?.helperRewardReleased !== true && proof?.helperReward?.released !== true) {
    return '';
  }
  return safeText(
    proof?.helperReward?.copy,
    'Helper reward released after verification',
  );
};

const buildReceiptRows = ({
  proof,
  regionLabel,
  acceptedDuration,
  completedDuration,
  helperRewardCopy,
}) => {
  const rows = [
    {
      label: 'What happened',
      value: safeText(proof?.taskLabel, 'Local task completed'),
    },
    { label: 'Where', value: regionLabel },
  ];
  if (acceptedDuration) {
    rows.push({ label: 'Accepted', value: `In ${acceptedDuration}` });
  }
  if (completedDuration) {
    rows.push({ label: 'Completed', value: `In ${completedDuration}` });
  }
  if (helperRewardCopy) {
    rows.push({ label: 'Helper reward', value: helperRewardCopy });
  }
  rows.push({ label: 'Verification', value: 'JG LocalOps completion' });
  const proofId = shortenProofId(proof?.proofId);
  if (proofId) rows.push({ label: 'Proof ID', value: proofId });
  return rows;
};

export default function LocalOpsProofLanding() {
  const { proofId } = useParams();
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const prevBodyBg = document.body.style.background;
    const prevHtmlBg = document.documentElement.style.background;
    document.body.style.background = '#050912';
    document.documentElement.style.background = '#050912';
    return () => {
      document.body.style.background = prevBodyBg;
      document.documentElement.style.background = prevHtmlBg;
    };
  }, []);

  useEffect(() => {
    if (!proofId) {
      setLoading(false);
      setNotFound(true);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    fetch(`${getBackendOrigin()}/share/resolve/localops/proof/${proofId}`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => {
        if (!res.ok) throw new Error('not-found');
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        setProof(json?.data || null);
        setNotFound(!json?.data);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [proofId]);

  const regionLabel = safeText(proof?.regionLabel, 'your area');
  const regionSlug = safeText(proof?.regionSlug, 'camden');
  const headline = buildProofHeadline(proof, regionLabel);
  const velocityChips = buildVelocityLine(proof);
  const acceptedDuration = formatDuration(
    proof?.acceptedInSeconds ?? proof?.velocity?.acceptedInSeconds,
  );
  const completedDuration = formatDuration(
    proof?.completedInSeconds ?? proof?.velocity?.completedInSeconds,
  );
  const helperRewardCopy = getHelperRewardCopy(proof);
  const helper = proof?.helper || null;
  const helperLine = helper?.handle
    ? `Completed by ${helper.handle}`
    : proof?.helperFallback || FALLBACK_HELPER_COPY;
  const postTaskPath = buildPostTaskPath(regionSlug);
  const heroMedia = useMemo(() => normalizeMedia(proof), [proof]);
  const receiptRows = buildReceiptRows({
    proof,
    regionLabel,
    acceptedDuration,
    completedDuration,
    helperRewardCopy,
  });

  if (loading) {
    return (
      <main className="localops-proof-page">
        <section className="localops-proof-card localops-proof-card--loading">
          <JGLocalOpsMark className="localops-proof-mark" />
          <p>Loading completion proof...</p>
        </section>
      </main>
    );
  }

  if (notFound || !proof) {
    return (
      <main className="localops-proof-page">
        <section className="localops-proof-card">
          <JGLocalOpsMark className="localops-proof-mark" />
          <h1>Completion proof unavailable</h1>
          <p>
            This proof link may no longer be available, or the public proof is
            not share-safe.
          </p>
          <p className="localops-proof-brand-line">{PROOF_LINE}</p>
          <a className="localops-proof-cta" href={postTaskPath}>
            Post a Local Task
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="localops-proof-page">
      <section className="localops-proof-card" data-testid="localops-proof-page">
        <JGLocalOpsMark className="localops-proof-mark" />
        <div className="localops-proof-body">
          <p className="localops-proof-kicker">Verified LocalOps completion</p>
          <span className="localops-proof-badge">
            VERIFIED LOCALOPS COMPLETION
          </span>
          <h1>{headline}</h1>
          <p className="localops-proof-brand-line">{PROOF_LINE}</p>
          <div className="localops-proof-chip-row" aria-label="Proof facts">
            {velocityChips.map((chip) => (
              <span className="localops-proof-chip" key={chip}>
                {chip}
              </span>
            ))}
            {helperRewardCopy ? (
              <span className="localops-proof-chip">{helperRewardCopy}</span>
            ) : null}
          </div>

          <div className="localops-proof-layout">
            <div className="localops-proof-media-panel">
              {heroMedia ? (
                <>
                  <div className="localops-proof-media-label">
                    Completion media
                  </div>
                  {heroMedia.type === 'video' ? (
                    <video
                      className="localops-proof-media"
                      src={heroMedia.url}
                      controls
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      className="localops-proof-media"
                      src={heroMedia.url}
                      alt=""
                    />
                  )}
                </>
              ) : (
                <div
                  className="localops-proof-fallback"
                  data-testid="localops-proof-fallback"
                >
                  <span>No public media shown</span>
                  <strong>
                    This proof page uses a privacy-safe completion summary.
                  </strong>
                </div>
              )}
            </div>

            <section className="localops-proof-receipt">
              <h2>LocalOps Action Receipt</h2>
              {!acceptedDuration && !completedDuration ? (
                <p>{FALLBACK_SUMMARY}</p>
              ) : null}
              <div className="localops-proof-receipt-rows">
                {receiptRows.map((row) => (
                  <div className="localops-proof-receipt-row" key={row.label}>
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="localops-proof-helper-chip">
            {helper?.profilePhoto ? (
              <img src={helper.profilePhoto} alt="" />
            ) : (
              <span className="localops-proof-helper-avatar">JG</span>
            )}
            <div>
              <strong>{helperLine}</strong>
              <span>{VERIFICATION_COPY}</span>
            </div>
          </div>

          <section className="localops-proof-cta-block">
            <h2>{CTA_HEADLINE}</h2>
            <p>{CTA_BODY}</p>
            <a className="localops-proof-cta" href={postTaskPath}>
              Post a Local Task
            </a>
            <p>{CTA_FOOTER}</p>
          </section>

          <div className="localops-proof-actions">
            {helper?.profilePath ? (
              <a className="localops-proof-secondary" href={helper.profilePath}>
                View helper profile
              </a>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
}
